import React, { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { PhoneCall, CheckCircle, DollarSign, CalendarClock, TrendingUp, ShieldCheck, ArrowRight } from "lucide-react";
import { KPIS, DAILY_CALLS, PUBLISHER, MY_OFFERS, money } from "../demoData";
import { Card, CardHead, PageTitle, Badge } from "../ui";

const RANGES = [["today", "Today"], ["week", "This Week"], ["month", "This Month"]];

const Stat = ({ icon: Icon, label, value, accent, testId }) => (
  <Card className="p-5" testId={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] sm:text-xs uppercase tracking-wider text-slate-500 truncate pr-2">{label}</p>
      <Icon className={`w-4 h-4 flex-shrink-0 ${accent}`} />
    </div>
    <p className="mt-2 text-2xl font-bold text-white tabular-nums">{value}</p>
  </Card>
);

export default function Overview({ goTo }) {
  const [range, setRange] = useState("week");
  const k = KPIS[range];
  return (
    <>
      <PageTitle title={`Welcome back, ${PUBLISHER.name.split(" ")[0]}`} sub={`${PUBLISHER.company} · ${PUBLISHER.payoutTerms}`}
        right={
          <div className="inline-flex rounded-xl border border-slate-800 bg-[#0f141c] p-1" data-testid="overview-range">
            {RANGES.map(([key, label]) => (
              <button key={key} onClick={() => setRange(key)} data-testid={`overview-range-${key}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${range === key ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>{label}</button>
            ))}
          </div>
        } />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={PhoneCall} label="Total Calls" value={k.calls.toLocaleString()} accent="text-blue-400" testId="stat-calls" />
        <Stat icon={CheckCircle} label="Qualified Calls" value={k.qualified.toLocaleString()} accent="text-emerald-400" testId="stat-qualified" />
        <Stat icon={DollarSign} label="Earnings" value={money(k.earnings)} accent="text-purple-400" testId="stat-earnings" />
        <Stat icon={CalendarClock} label="Next Payout" value={money(KPIS.nextPayout.amount)} accent="text-amber-400" testId="stat-next-payout" />
      </div>
      <div className="mt-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2" testId="overview-chart">
          <CardHead icon={TrendingUp} title="Calls — Last 13 Days" right={<span className="hidden sm:inline text-xs text-slate-500">Qualified after 120s post-IVR</span>} />
          <div className="h-64 p-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={DAILY_CALLS} margin={{ left: -20, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} /><stop offset="100%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gQ" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4ade80" stopOpacity={0.35} /><stop offset="100%" stopColor="#4ade80" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="#1e293b" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0f141c", border: "1px solid #1e293b", borderRadius: 12, fontSize: 12 }} />
                <Area type="monotone" dataKey="calls" name="Calls" stroke="#3b82f6" strokeWidth={2} fill="url(#gC)" />
                <Area type="monotone" dataKey="qualified" name="Qualified" stroke="#4ade80" strokeWidth={2} fill="url(#gQ)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5 flex flex-col" testId="overview-quality">
          <div className="flex items-center gap-2 text-sm font-semibold text-white"><ShieldCheck className="w-4 h-4 text-emerald-400" /> CallIntell™ Quality Score</div>
          <p className="mt-4 text-5xl font-extrabold text-emerald-400">{KPIS.qualityScore}<span className="text-xl text-slate-500">/100</span></p>
          <p className="mt-2 text-sm text-slate-400">Excellent — your traffic is in the top 10% of the network.</p>
          <div className="mt-4 rounded-xl bg-[#0b0f15] border border-slate-800 p-4">
            <p className="text-xs text-slate-500">Next payout</p>
            <p className="text-lg font-bold text-white">{money(KPIS.nextPayout.amount)}</p>
            <p className="text-xs text-slate-400">on {KPIS.nextPayout.date} · {PUBLISHER.payoutTerms}</p>
          </div>
          <button onClick={() => goTo("quality")} className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 font-semibold" data-testid="overview-quality-link">View details <ArrowRight className="w-4 h-4" /></button>
        </Card>
      </div>
      <Card className="mt-4" testId="overview-top-offers">
        <CardHead icon={PhoneCall} title="Top Offers This Week" right={<button onClick={() => goTo("offers")} className="text-xs text-blue-400 font-semibold" data-testid="overview-offers-link">View all</button>} />
        <div className="divide-y divide-slate-800">
          {MY_OFFERS.slice(0, 4).map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
              <span className="text-slate-200 truncate">{o.name}</span>
              <div className="flex items-center gap-4 flex-shrink-0">
                <span className="hidden sm:inline text-slate-400 tabular-nums">{o.calls.toLocaleString()} calls</span>
                <span className="text-white font-semibold">${o.payout}/call</span>
                <Badge>{o.status}</Badge>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}
